import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coins, Heart, Flame, ShieldCheck, Crown, Scale, ChevronDown, TriangleAlert } from "lucide-react";
import { Reveal } from "./Reveal";
import { whatsappLink } from "../config/site";
import { useLang } from "../config/i18n";

const ICONS = {
  prosperity: Coins,
  love: Heart,
  cleansing: Flame,
  protection: ShieldCheck,
  power: Crown,
  justice: Scale,
};

export const Trabalhos = () => {
  const { t } = useLang();
  const [open, setOpen] = useState("prosperity");

  return (
    <section id="trabalhos" data-testid="works-section" className="relative py-24 sm:py-36">
      <div className="max-w-4xl mx-auto px-6">
        <Reveal className="text-center">
          <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{t.trabalhos.overline}</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#F7F7F8] leading-snug mb-6">
            {t.trabalhos.title}
          </h2>
          <p className="max-w-2xl mx-auto text-[15px] sm:text-base text-[#A3A3AF] leading-relaxed font-light">
            {t.trabalhos.intro}
          </p>
        </Reveal>

        <div data-testid="works-category-tabs" className="mt-14 flex flex-col gap-3">
          {t.trabalhos.categories.map(({ id, title, items }) => {
            const Icon = ICONS[id];
            const isOpen = open === id;
            return (
              <Reveal key={id}>
                <div
                  className={`rounded-xl border bg-[#121216] transition-colors duration-300 ${
                    isOpen ? "border-[#C5A059]/45" : "border-[#22222B] hover:border-[#C5A059]/30"
                  }`}
                >
                  <button
                    data-testid={`works-tab-${id}`}
                    onClick={() => setOpen(isOpen ? "" : id)}
                    className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 min-h-[68px] text-left"
                  >
                    <span className="flex items-center gap-4">
                      <Icon size={20} className="text-[#C5A059] shrink-0" strokeWidth={1.4} />
                      <span className="font-heading text-sm sm:text-base tracking-[0.12em] uppercase text-[#F7F7F8]">
                        {title}
                      </span>
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-[#C5A059] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div data-testid={`works-card-${id}`} className="px-6 sm:px-8 pb-8 pt-1">
                          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4 border-t border-[#22222B] pt-6">
                            {items.map((item) => (
                              <li key={item} className="flex items-start gap-3 text-sm sm:text-[15px] text-[#D1D1DB] font-light">
                                <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#C5A059] shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div data-testid="works-warning-box" className="mt-10 p-7 sm:p-9 rounded-xl bg-[#16161B] border border-[#D9A94A]/25">
            <div className="flex items-center gap-3 mb-5">
              <TriangleAlert size={20} className="text-[#D9A94A]" strokeWidth={1.6} />
              <h3 className="font-heading text-base sm:text-lg tracking-[0.12em] uppercase text-[#F7F7F8]">
                {t.trabalhos.warning.title}
              </h3>
            </div>
            <ul className="space-y-3">
              {t.trabalhos.warning.items.map((w) => (
                <li key={w.slice(0, 20)} className="flex items-start gap-3 text-sm text-[#A3A3AF] leading-relaxed font-light">
                  <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#D9A94A] shrink-0" />
                  {w}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="mt-12 text-center">
          <p className="text-sm text-[#6E6E7D] font-light italic mb-8 max-w-xl mx-auto">
            {t.trabalhos.note}
          </p>
          <a
            data-testid="works-cta-btn"
            href={whatsappLink(t.trabalhos.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold"
          >
            {t.trabalhos.cta}
          </a>
        </Reveal>
      </div>
    </section>
  );
};
